/**
 * In-app Help: step-by-step guides per module. Each guide lists the screens
 * (routes) it explains, so the "?" button in the top bar opens the right one.
 * Keep the wording in sync with the button labels on screen.
 */
export interface Guide {
  id: string;
  module: string;
  title: string;
  /** Route prefixes this guide explains, for the contextual "?" button. */
  routes: string[];
  /** Shown only to people holding one of these permissions (none = everyone). */
  permissions?: string[];
  /** Restricted module: shown only to holders of this grant. */
  grant?: string;
  intro?: string;
  steps: string[];
  tips?: string[];
}

export const GUIDES: Guide[] = [
  // ─── Getting started ──────────────────────────────────────────────────
  {
    id: 'start', module: 'Getting started', title: 'Signing in and finding your way', routes: ['/account'],
    steps: [
      'Sign in with your email and password. Officials, the Treasurer, administrators and anyone with VAW Desk or BADAC access also enter the 6-digit code from their authenticator app.',
      'The menu on the left shows only the modules your role can use. On a phone, tap the ☰ button at the top.',
      'The Dashboard shows what needs action today. Tap any tile to open the list behind it.',
      'Use the sun/moon button at the top to switch between light and dark mode.',
      'Tap ? at the top of any screen to open the guide for that screen.',
    ],
    tips: ['Forgot your password? Ask your barangay System Administrator to reset it from Setup → Users.'],
  },
  {
    id: 'dashboard', module: 'Getting started', title: 'Reading the dashboard', routes: ['/'], permissions: ['dashboard.view'],
    intro: 'Colors follow one rule everywhere: red = act now, amber = soon, green = on track, blue = information.',
    steps: [
      'Certificates: requests waiting for approval, approved but not yet released, and anything past the Citizen\'s Charter time.',
      'Peace and order: open Lupon cases, deadlines due within 3 days, and deadlines already passed. The list on the right shows the next legal deadline per case.',
      'Population: inhabitants, households and sectors, with the purok chart, population pyramid and sector chart.',
      'Businesses: active businesses and those still without a clearance this year.',
      'Tap a tile or a list item to go straight to the record.',
    ],
  },

  // ─── Residents ────────────────────────────────────────────────────────
  {
    id: 'add-resident', module: 'Residents', title: 'Adding a resident', routes: ['/residents/new', '/residents'], permissions: ['residents.manage'],
    steps: [
      'Go to Residents → Residents and tap New resident.',
      'Name and birth: enter the full name, sex, birth date and civil status.',
      'Household: pick an existing household, or leave it empty and add them to one later. Set the relationship to the household head.',
      'Other details: contact number, occupation, and sectors (senior citizen, PWD, solo parent, 4Ps, registered voter …). Age-based sectors such as minor and senior are worked out from the birth date.',
      'Data privacy: show or read the barangay\'s privacy notice to the resident, choose the lawful basis, then tick that the notice was given.',
      'Tap Save. A resident number (RES-…) is assigned.',
    ],
    tips: [
      'If someone with the same name and birth date already exists, you will be warned. Open the existing record instead of creating a duplicate.',
      'The PhilSys number is stored encrypted; only staff with permission can see it in full.',
    ],
  },
  {
    id: 'households', module: 'Residents', title: 'Households', routes: ['/households'], permissions: ['residents.view'],
    steps: [
      'Go to Residents → Households and tap New household.',
      'Choose the purok or sitio, and enter the house number and street.',
      'Open the household and tap Add member to attach residents, or set the household when adding a resident.',
      'Use Edit address when the family moves within the barangay.',
    ],
  },
  {
    id: 'resident-record', module: 'Residents', title: 'Updating a resident: transfer, death, ID card, merge', routes: ['/residents/'], permissions: ['residents.view'],
    steps: [
      'Open the resident from Residents → Residents. Use the search box (name, resident number) and the filters at the top.',
      'Tap Edit to correct details. Every change is kept in the history.',
      'To record a transfer out or a death, tap Moved out / died (or Moved back in for a return). The date you enter decides which month\'s report it counts in.',
      'Under ID card, tap Issue ID (or Reissue for a lost card) to print a resident ID with a QR code. Anyone can scan the QR to check that the card is genuine.',
      'Duplicates: staff with merge permission tap Merge duplicate… and choose the record to keep.',
    ],
  },
  {
    id: 'registry-reports', module: 'Residents', title: 'RBI reports (Forms A, B, C)', routes: ['/registry/reports'], permissions: ['residents.view'],
    steps: [
      'Go to Residents → Registry Reports.',
      'Choose the period at the top (the quarter is preselected).',
      'Download RBI Form A (household records), Form B (individual records) or Form C (monitoring report), or Excel for the full list.',
    ],
  },
  {
    id: 'import', module: 'Residents', title: 'Importing a resident list from Excel', routes: ['/registry/import'], permissions: ['residents.import'],
    steps: [
      'Go to Residents → Import and download the Template.',
      'Fill one row per resident. Keep the column headings.',
      'Upload the file and tap Check the file. Nothing is saved yet: you see every row with its problems.',
      'Fix the file and check again, then tap Import. Possible duplicates are listed for review.',
    ],
  },

  // ─── Certificates ─────────────────────────────────────────────────────
  {
    id: 'new-request', module: 'Certificates', title: 'Issuing a certificate or clearance', routes: ['/documents/new', '/documents'], permissions: ['documents.request', 'documents.approve', 'documents.release'],
    intro: 'Every request goes through: received → for approval → approved → paid (or exempt) → released.',
    steps: [
      'Go to Certificates → Requests and tap New request.',
      'Pick the resident (or the business for a business clearance) and the document type, then the purpose.',
      'Tap Create request. A tracking number (DOC-…) is assigned and the Citizen\'s Charter clock starts.',
      'If the requirements are complete, tap Application complete. It goes to the Punong Barangay (or signatory) for approval.',
      'The signatory opens it and taps Approve, or Return with a reason.',
      'At the counter, tap Record payment (or mark the fee exempt, e.g. first-time jobseekers under RA 11261), then Print and Release.',
    ],
    tips: [
      'Every printed document has a QR code. Anyone can scan it to check that it is genuine and still valid.',
      'Issuance log (Excel) lists everything released in a period.',
    ],
  },
  {
    id: 'businesses', module: 'Certificates', title: 'Businesses and business clearance', routes: ['/businesses'], permissions: ['business.view', 'business.manage'],
    steps: [
      'Go to Certificates → Businesses and tap New business. Enter the trade name, owner, address and line of business.',
      'To issue this year\'s clearance, create a New request with the business clearance type and pick the business.',
      'The Dashboard shows businesses still without a clearance this year.',
    ],
  },

  // ─── Peace & Order ────────────────────────────────────────────────────
  {
    id: 'blotter', module: 'Peace & Order', title: 'Logging a blotter entry', routes: ['/blotter'], permissions: ['blotter.view', 'blotter.create'],
    steps: [
      'Go to Peace & Order → Blotter and tap Log incident.',
      'Enter the date and time, nature, place, who reported it, and the narrative in the reporting person\'s words.',
      'Under People involved, add the complainant, respondent, victims or witnesses.',
      'Tap Save entry. It gets the next number for the year (BLT-…).',
      'Entries can never be edited or deleted. To correct or add information, open the entry and use Add under Amendments.',
      'From the entry you can refer it To the Lupon (creates a KP case), To the PNP (prints a referral slip), or to the VAW Desk / BADAC.',
    ],
    tips: ['Tanods see only the entries they logged. A VAW Desk referral hides names and the narrative from general lists.'],
  },
  {
    id: 'tanod', module: 'Peace & Order', title: 'Tanod duty roster and patrols', routes: ['/tanod'], permissions: ['blotter.view', 'blotter.create'],
    steps: [
      'Go to Peace & Order → Tanod Duty.',
      'Pick a date in the week, then add each duty: date, shift, area and the tanods (separate names with commas).',
      'Tap Print week for the roster to post at the hall.',
      'After each patrol, fill in Patrol log (area, what was observed, tanods) and tap Log patrol now.',
    ],
  },
  {
    id: 'kp', module: 'Peace & Order', title: 'Running a Lupon (KP) case', routes: ['/kp'], permissions: ['kp.view'],
    intro: 'The system counts the legal deadlines for you: 15 days of mediation, 15 days for the Pangkat (extendable once by 15), 10 days to repudiate, and 6 months to execute.',
    steps: [
      'File the case: Peace & Order → Lupon Cases → File complaint, or open a blotter entry and refer it To the Lupon.',
      'Tick any Sec. 408 facts that apply. If the Lupon may have no authority, you will be warned before docketing.',
      'Open the case. Under Next step, tap Start mediation on the date of the first meeting with the Punong Barangay.',
      'Issue notice / summons (Forms 8, 9), print them, and tap Mark served once delivered.',
      'Record hearing after each meeting, noting who was present.',
      'If mediation fails, tap Mediation failed: constitute Pangkat and choose a chairperson, secretary and member from the Lupon roster.',
      'When the parties agree, tap Record settlement (amicable or arbitration award) and print Form 16 or 15.',
      'Within 10 days a party may repudiate. After that, tap Record execution once the terms are carried out.',
      'If there is no settlement, the respondent does not appear, or the settlement was repudiated, tap Issue Certificate to File Action and print Form 20. Its QR code can be checked online.',
    ],
    tips: ['Pangkat members without full KP rights see only the cases they sit on.', 'Lupon Report gives the period statistics and an Excel file.'],
  },

  // ─── Social services ──────────────────────────────────────────────────
  {
    id: 'programs', module: 'Social Services', title: 'Programs and beneficiary lists', routes: ['/social/programs'], permissions: ['social.view', 'social.manage'],
    steps: [
      'Go to Social Services → Programs and tap New program (e.g. Social pension for seniors).',
      'Open it and tap Add beneficiaries. Filter the registry by sector, purok and age, tick the residents, then tap Add.',
      'Each membership has a start date. Use End (with the reason) when someone leaves the program; past members stay on record.',
      'Tap Excel for the beneficiary list.',
    ],
  },
  {
    id: 'assistance', module: 'Social Services', title: 'Recording assistance', routes: ['/social/assistance'], permissions: ['social.view', 'social.manage'],
    steps: [
      'Tap Record assistance. Scan the resident\'s ID or search by name.',
      'Choose the kind (cash, goods, medical, burial, educational). Cash needs the amount.',
      'If it is based on a certificate of indigency, choose it, and enter the receiving or referring agency.',
      'Enter who released it and how it was confirmed (ID scan or signature).',
    ],
  },
  {
    id: 'distributions', module: 'Social Services', title: 'Relief distributions', routes: ['/social/distributions'], permissions: ['social.view', 'social.manage'],
    steps: [
      'Tap New distribution: title, what each claimant receives, and whether it is one per household or one per person. Link it to a program to limit it to its beneficiaries.',
      'At the venue, open it and scan each claimant\'s ID, or search by name, then release the package.',
      'If the person (or, per household, someone in the household) already claimed, you are warned. Release anyway only with a reason.',
      'Tap Close when done, and Excel for the list of claims.',
    ],
  },

  // ─── DRRM ─────────────────────────────────────────────────────────────
  {
    id: 'zones', module: 'DRRM', title: 'Hazard zones and vulnerable lists', routes: ['/drrm/zones'], permissions: ['drrm.view', 'drrm.manage'],
    steps: [
      'Go to DRRM → Hazard Zones and tap New zone (e.g. Riverside flood zone, high risk).',
      'Open the zone and add households: tick whole puroks, or search a member\'s name to add one household.',
      'The list shows each household head, contact number and the vulnerable members (children under 5, seniors, PWD, pregnant/lactating, solo parents).',
      'Tap Vulnerable list to print it for pre-emptive evacuation.',
    ],
  },
  {
    id: 'evacuation', module: 'DRRM', title: 'Evacuation and situation reports', routes: ['/drrm/incidents', '/drrm/centers'], permissions: ['drrm.view', 'drrm.manage'],
    steps: [
      'Register the evacuation centers once, with their capacity (DRRM → Evacuation Centers).',
      'When a typhoon or other incident hits, go to DRRM → Incidents & Evacuation and tap Open incident.',
      'At the center, choose it, then scan any family member\'s ID or search by name. The whole household is checked in. Add others who are not in the registry.',
      'The headcount per center, by sex and age group, updates as families arrive. Full centers turn red. The Dashboard shows the totals at the top.',
      'Tap Check out as families go home.',
      'Tap New situation report: evacuation figures fill in from the check-ins; enter damage, casualties, needs and actions. Print it (PDF) or send the Excel to the city/municipal DRRMO.',
      'Close the incident when it is over.',
    ],
  },
  {
    id: 'fund', module: 'DRRM', title: 'BDRRM fund', routes: ['/drrm/fund'], permissions: ['drrm.view', 'drrm.manage'],
    steps: [
      'The fund is the budget accounts tagged "5% BDRRM Fund" under Treasury → Budget, at least 5% of estimated revenue.',
      'Vouchers charged to those accounts reduce the balance, shown here and on the Dashboard.',
    ],
  },

  // ─── Legislation ──────────────────────────────────────────────────────
  {
    id: 'sessions', module: 'Legislation', title: 'Sessions, roll call and minutes', routes: ['/legislation/sessions'], permissions: ['legislation.view', 'legislation.manage'],
    intro: 'Regular sessions are held at least twice a month. The Dashboard counts them for you.',
    steps: [
      'Go to Legislation → Sessions and tap Schedule session. Enter the date, time, venue and agenda.',
      'On the day, open the session, tick who is present, and tap Record roll call. Quorum (a majority of all members) is computed.',
      'Write the minutes on the session page.',
      'At the next session held with a quorum, open it: the earlier minutes appear under "Approve minutes of earlier sessions". Tap Approve.',
      'Approved minutes and their attendance are locked and cannot be changed.',
    ],
  },
  {
    id: 'ordinances', module: 'Legislation', title: 'Ordinances, resolutions and executive orders', routes: ['/legislation'], permissions: ['legislation.view', 'legislation.manage'],
    steps: [
      'Go to Legislation → Ordinances & Resolutions and tap New draft. The number (e.g. Ordinance No. 2026-004) is assigned right away.',
      'Edit the draft as needed, then tap Mark approved and choose the session (held with a quorum) where it was approved.',
      'For an ordinance, tap Submitted for review with the date the city/municipal council received it. After 30 days without action it is deemed approved (LGC Sec. 57), and the Dashboard tells you to mark it effective.',
      'Tap Mark effective on the date it takes effect. Attach the signed copy under Attachments.',
      'To repeal a measure, first approve the repealing one, then open the old one and tap Repealed.',
      'Search looks in numbers, titles and the full text.',
    ],
  },
  {
    id: 'assembly', module: 'Legislation', title: 'Barangay Assembly', routes: ['/legislation/assemblies'], permissions: ['legislation.view', 'legislation.manage'],
    intro: 'Held at least twice a year, in March and October.',
    steps: [
      'Go to Legislation → Barangay Assembly and tap New assembly.',
      'At the venue, open the assembly. Scan each resident ID with a QR scanner into the "Scan" box. Each person is recorded once.',
      'Residents without an ID: search by name and tap Present.',
      'Write the minutes, attach the semestral report, and tap Publish on portal when ready.',
    ],
  },
  {
    id: 'committees', module: 'Legislation', title: 'Committees', routes: ['/legislation/committees'], permissions: ['legislation.view', 'legislation.manage'],
    steps: [
      'Tap New committee, then Add member with the role and the start of the term. A committee has one chair at a time.',
      'Use "end term" when a member leaves; past members stay listed in grey.',
      'Tap File report to record committee reports.',
    ],
  },

  // ─── Treasury ─────────────────────────────────────────────────────────
  {
    id: 'booklets', module: 'Treasury', title: 'Setting up OR booklets', routes: ['/treasury/booklets'], permissions: ['finance.manage'],
    intro: 'Do this first. Every official receipt must come from a booklet recorded here, so payments are refused until one exists.',
    steps: [
      'Go to Treasury → OR Booklets and tap Add booklet.',
      'Enter the first and last OR numbers printed on the booklet (e.g. 1234551 to 1234600), the accountable officer and the date received.',
      'Ranges cannot overlap. The screen shows how many receipts are left and the next unused number.',
      'When fewer than 25 receipts remain, the Dashboard reminds you to request a new booklet.',
    ],
  },
  {
    id: 'collections', module: 'Treasury', title: 'Issuing official receipts', routes: ['/treasury'], permissions: ['finance.view', 'finance.collect', 'finance.manage'],
    steps: [
      'Certificate fees: open the certificate and tap Record payment with the OR number. The collection appears here automatically.',
      'Other collections (rentals, fines…): Treasury → Collections → Issue OR. The next unused OR number is suggested.',
      'An OR number outside the issued booklets, or one already used, is refused.',
      'A spoiled or wrong receipt is never edited or deleted. Tap Cancel and give the reason. If it paid a certificate, the certificate goes back to "payment due".',
      'Tap Daily report to print the Report of Collections and Deposits for the day.',
    ],
  },
  {
    id: 'deposits', module: 'Treasury', title: 'Depositing collections', routes: ['/treasury/deposits'], permissions: ['finance.view', 'finance.manage', 'finance.collect'],
    steps: [
      'Go to Treasury → Deposits. Receipts not yet deposited are listed.',
      'Tick the receipts covered by the deposit slip (or tick all), enter the bank and the slip number, then tap Record deposit.',
      'The Dashboard shows cash not yet deposited and turns amber when collections are more than a day old.',
    ],
  },
  {
    id: 'budget', module: 'Treasury', title: 'Entering the annual budget', routes: ['/treasury/budget'], permissions: ['finance.view', 'finance.manage', 'finance.approve'],
    steps: [
      'Go to Treasury → Budget, choose the year and tap Add account for each appropriation in the approved budget ordinance.',
      'Choose the class (PS, MOOE, CO) and tag the fund: General, 20% Development Fund, 5% BDRRM Fund or 10% SK Fund.',
      'The page shows each fund as a share of the budget, and for each account what is obligated and what is still available.',
    ],
  },
  {
    id: 'vouchers', module: 'Treasury', title: 'Disbursement vouchers', routes: ['/treasury/vouchers'], permissions: ['finance.view', 'finance.manage', 'finance.approve'],
    steps: [
      'The Treasurer taps Prepare voucher, chooses the budget account (its available balance is shown), the payee, the particulars and the amount.',
      'If the amount is more than what is left on the account, an override reason is required (e.g. supplemental budget approved).',
      'The Punong Barangay opens Vouchers → For approval and taps Approve. Nobody can approve a voucher they prepared.',
      'Once the check or ADA is issued, the Treasurer taps Pay and enters the check number and date.',
      'Paid vouchers feed the cashbook, the budget balances and the FDP reports.',
    ],
  },
  {
    id: 'cashbook', module: 'Treasury', title: 'Cashbook, income and cash advances', routes: ['/treasury/cashbook', '/treasury/income'], permissions: ['finance.view', 'finance.manage'],
    steps: [
      'Treasury → Income & Advances: record income that does not go through an OR (NTA share, real property tax share, grants), with the ADA or credit advice number.',
      'Grant cash advances with a liquidation date, and tap Liquidate when the officer submits receipts. Overdue advances show in red on the Dashboard.',
      'Treasury → Cashbook shows cash in treasury (collections less deposits) and cash in bank (deposits and income less checks), each with a running balance. Tap Print for the COA layout.',
    ],
  },
  {
    id: 'fdp', module: 'Treasury', title: 'Full Disclosure Policy reports', routes: ['/treasury/fdp'], permissions: ['finance.view', 'finance.manage', 'finance.approve', 'portal.manage'],
    steps: [
      'Go to Treasury → Full Disclosure. Each required report is listed: annual budget, summary of income and expenditures, 20% development fund utilization, itemized monthly collections and disbursements.',
      'Set the period (e.g. 2026, 2026-Q3, 2026-09) and tap PDF.',
      'Post it in three conspicuous places and on the FDP portal, then tap Log posting for each place. The posting log is your proof of compliance.',
    ],
  },

  // ─── Projects & Property ──────────────────────────────────────────────
  {
    id: 'projects', module: 'Projects & Property', title: 'Projects and site reports', routes: ['/projects'], permissions: ['projects.view', 'projects.manage'],
    steps: [
      'Go to Projects & Property → Projects and tap New project: title, fund source (e.g. 20% Development Fund), budget, start and target completion.',
      'After each site inspection, open the project and tap Report progress with the date and percent complete. You are then asked for the site photos.',
      'When the Treasurer prepares a voucher for the project, they choose it under "Charge to project". Paid vouchers make up the financial progress.',
      'A project more than 10 points behind the schedule, or past its target date, is flagged on the Dashboard.',
      'A 100% report completes the project.',
    ],
  },
  {
    id: 'procurement', module: 'Projects & Property', title: 'Procurement plan, requests and awards', routes: ['/procurement'], permissions: ['projects.view', 'projects.manage'],
    steps: [
      'Procurement plan: add each item of the Annual Procurement Plan with the mode, quarter, fund and estimated budget.',
      'Purchase requests: tap New request and link it to the APP item.',
      'When the winner is chosen, tap Award on the request (or Record award). The award cannot exceed the request. If linked to a project, the contractor is set on it.',
      'The APP and the notices of award print under Treasury → Full Disclosure.',
    ],
  },
  {
    id: 'property', module: 'Projects & Property', title: 'Property and the physical count', routes: ['/property'], permissions: ['property.view', 'property.manage'],
    steps: [
      'Tap Register item: description, category, date acquired, cost, custodian and location. Items of ₱50,000 and up print a PAR; below that, an ICS.',
      'Open an item to Transfer it to another custodian (enter the PAR/ICS number they signed), change its condition, or dispose of it. The record and its history are kept.',
      'Once a year, go to Physical Count → Start count and name the inventory team.',
      'For each item, tap Yes or No for "Found?". Adjust the condition and location as found. Differences are listed as you go.',
      'Tap Close count to apply the findings to the records, then Report to print it for submission.',
    ],
  },

  // ─── VAW Desk (restricted) ────────────────────────────────────────────
  {
    id: 'vaw', module: 'VAW Desk', title: 'Opening a VAW case', routes: ['/vaw/new', '/vaw'], grant: 'vaw.access',
    intro: 'Only people the Punong Barangay has given VAW Desk access can see this module. Names and narratives are encrypted, and every case you open or print is logged.',
    steps: [
      'Go to VAW Desk → Cases. Blotter entries referred to the VAW Desk appear at the top: tap Open case to start from one without retyping.',
      'Otherwise tap New case.',
      'Fill in the survivor (name, sex, age, address, a safe contact number), the respondent and their relationship to the survivor.',
      'Tick the forms of violence (physical, sexual, psychological, economic) and write the narrative in the survivor\'s words.',
      'Tap Open case. A case number (VAW-…) is assigned.',
      'Use the Logbook on the case for every action taken: home visits, counselling, calls.',
    ],
  },
  {
    id: 'bpo', module: 'VAW Desk', title: 'Issuing a Barangay Protection Order (BPO)', routes: ['/vaw/'], grant: 'vaw.access',
    intro: 'RA 9262: the BPO is issued on the day the application is filed and is valid for 15 days, counting the day of issue.',
    steps: [
      'Open the case and tap Issue BPO.',
      'Enter the date the application was filed (normally today) and choose who issues it.',
      'If a Kagawad issues it because the Punong Barangay is unavailable, write the attestation. It is required and printed on the BPO.',
      'Tap Issue BPO, then Print BPO and have it served personally on the respondent.',
      'Tap Mark served with the date and the name of the person who served it.',
      'Three days before it ends, the case and the Dashboard turn amber: help the survivor apply to the court for a Temporary Protection Order. Use End early if the court\'s order supersedes it.',
    ],
  },
  {
    id: 'vaw-referral', module: 'VAW Desk', title: 'Referrals and follow-up', routes: [], grant: 'vaw.access',
    steps: [
      'On the case, tap Refer. Choose the agency (C/MSWDO, PNP Women and Children Protection Desk, health facility, legal aid…), the office name, the purpose and a follow-up date.',
      'Tap Referral form to print it, with a feedback slip for the agency to return.',
      'When the agency replies, tap Record feedback and set the next follow-up if needed.',
      'Follow-ups that are due show in red on the case list and the Dashboard.',
    ],
  },
  {
    id: 'vaw-report', module: 'VAW Desk', title: 'Quarterly VAW report', routes: ['/vaw/report'], grant: 'vaw.access',
    steps: [
      'Go to VAW Desk → Quarterly Report and choose the year and quarter.',
      'The report has counts only: survivors by age and sex, forms of violence, relationship, referrals, BPOs. No names.',
      'Hover over a number to see which case numbers it counts.',
      'Tap Excel and submit the file to the DILG field office and the C/MSWDO.',
    ],
  },

  // ─── BADAC (restricted) ───────────────────────────────────────────────
  {
    id: 'badac', module: 'BADAC', title: 'Plan, meetings and activities', routes: ['/badac'], grant: 'badac.access',
    steps: [
      'Go to BADAC → Plan & Records. Under Plan of action, tap Add plan item for each item of this year\'s plan (target, timeline).',
      'Under Meetings & activities, tap Log meeting / activity after each one.',
      'For a meeting, list the attendance (names separated by commas) and write the minutes. Both are required.',
      'For an awareness activity or training, enter the number of participants and link it to the plan item it carries out.',
    ],
  },
  {
    id: 'badac-records', module: 'BADAC', title: 'Confidential records (surrender, CBDRP)', routes: [], grant: 'badac.access',
    steps: [
      'Under Records, tap New record: name, sex, age, address, category (voluntary surrender, referral, aftercare) and notes. The details are encrypted.',
      'Tap Update when the person starts or completes community-based rehabilitation (CBDRP), or the record is closed. The note is added to the history.',
      'Only BADAC grant holders can open this list, and every view is logged.',
    ],
  },
  {
    id: 'badac-report', module: 'BADAC', title: 'Monthly report to the C/MADAC', routes: ['/badac/report'], grant: 'badac.access',
    intro: 'The report for each month is due on the 5th of the next month. The Dashboard reminds you from the 1st and turns red after the 5th.',
    steps: [
      'Go to BADAC → Monthly Report. Last month is selected.',
      'Check the counts: meetings, activities and participants, new records, CBDRP movements, plan progress.',
      'Tap Excel and send the file to the City/Municipal Anti-Drug Abuse Council.',
      'Tap Mark as submitted. The reminder clears.',
    ],
  },

  // ─── Citizen portal ───────────────────────────────────────────────────
  {
    id: 'portal', module: 'Citizen Portal', title: 'Online accounts and requests', routes: ['/portal-admin/accounts'], permissions: ['portal.manage'],
    intro: 'Switch the portal on under Setup → Barangay Profile → Citizen portal. Residents then sign up at /portal on this site.',
    steps: [
      'A resident creates an account online. It starts as "waiting for verification" and cannot see or request anything.',
      'When they come to the hall with a valid ID, open Citizen Portal → Online Accounts and tap Verify.',
      'Choose their record (residents with the same birth date are listed first), enter the ID presented, then tap Verify and link.',
      'Verified residents can request certificates online. These appear under Certificates → Requests marked "online".',
      'On the request, set the Pick-up date: the resident sees it on the portal together with the status, requirements still to bring and the fee.',
      'Reject an account that is not from this barangay, or Disable one that is misused. The reason is shown to the resident.',
    ],
  },
  {
    id: 'announcements', module: 'Citizen Portal', title: 'Announcements and SMS', routes: ['/portal-admin/announcements'], permissions: ['portal.manage'],
    steps: [
      'Tap New announcement: title and message. Post it on the portal (also shown on the transparency board), send it by SMS, or both.',
      'Choose who: leave empty for everyone, or pick puroks, sectors (seniors, PWD, 4Ps…) and hazard zones. Groups from the VAW Desk or BADAC can never be chosen.',
      'For SMS, the number of mobile numbers, message parts and total cost are shown before sending. Tap Publish, then confirm.',
      'Until an SMS provider is contracted and set up by ITCSI, messages are logged as "not sent".',
    ],
  },
  {
    id: 'transparency', module: 'Citizen Portal', title: 'The public transparency board', routes: [], permissions: ['portal.manage', 'legislation.manage', 'finance.manage'],
    steps: [
      'The board is public at /transparency/<barangay> once the citizen portal is on. It shows the Citizen\'s Charter (from Documents & Fees), officials, announcements, and anything you publish.',
      'FDP reports: under Treasury → Full Disclosure, tap Log posting with "Citizen portal". A frozen PDF of the report is published with its posting date.',
      'Ordinances: open an approved measure and tap Publish under Record.',
      'Barangay Assembly: tap Publish on portal on the assembly to show its minutes and semestral report.',
    ],
  },
  {
    id: 'feedback', module: 'Citizen Portal', title: 'Feedback and satisfaction', routes: ['/portal-admin/feedback'], permissions: ['portal.manage', 'documents.request', 'documents.release'],
    steps: [
      'Feedback sent on the portal arrives under Citizen Portal → Feedback. Record walk-in or phone feedback with the button at the top.',
      'Tap Update to assign it, move it to In progress, and write the resolution when resolved. The resident sees the reply on the portal.',
      'Satisfaction per service shows the average rating and the share of 4–5 star ratings.',
    ],
  },

  // ─── Setup ────────────────────────────────────────────────────────────
  {
    id: 'setup-barangay', module: 'Setup', title: 'Barangay profile, puroks and fees', routes: ['/setup/barangay', '/setup/documents'], permissions: ['barangay.view', 'barangay.manage'],
    steps: [
      'Setup → Barangay Profile: name, city or municipality, hall address, contact number, the barangay seal and the city logo (used on every printed document).',
      'Add the puroks and sitios in the same screen. They are used for addresses, reports and hazard zones.',
      'Edit the privacy notice residents are shown when they are registered.',
      'Setup → Documents & Fees: set the fee, processing time and template for each document type.',
    ],
  },
  {
    id: 'setup-officials', module: 'Setup', title: 'Officials and staff', routes: ['/setup/officials'], permissions: ['officials.view', 'officials.manage'],
    steps: [
      'Setup → Officials & Staff lists the Punong Barangay, Kagawads, SK, Secretary, Treasurer, tanods and Lupon members with their terms.',
      'Add each person with their position and term. Tick "May sign certificates and clearances" for signatories.',
      'Lupon members with a current term make up the roster for Pangkat selection.',
    ],
  },
  {
    id: 'setup-users', module: 'Setup', title: 'Users, roles and permissions', routes: ['/setup/users', '/setup/roles'], permissions: ['users.view', 'users.manage'],
    steps: [
      'Setup → Users → Invite user: enter the email, name and role. A temporary password is shown once: give it to them privately. They must change it at first sign-in.',
      'Setup → Roles & Permissions: built-in roles follow the DILG setup (Punong Barangay, Secretary, Treasurer, Kagawad, Tanod, Lupon …). Tap New role or Copy to make your own.',
      'Ticking a sensitive permission (approve documents, collect fees, manage users …) makes MFA required for that role.',
      'A menu item appears only when its module is built and the role has the permission for it.',
    ],
    tips: ['VAW Desk and BADAC are not role permissions. The Punong Barangay grants them per person under Restricted Access.'],
  },
  {
    id: 'restricted', module: 'Setup', title: 'Restricted access (VAW Desk, BADAC)', routes: ['/restricted-access'],
    steps: [
      'Only the Punong Barangay (or someone already holding the grant) can give access to the VAW Desk or BADAC.',
      'Open Restricted Access, choose the person and the module, and confirm. Nobody can grant access to themselves.',
      'People holding a grant must use MFA. Every view of a restricted record is logged.',
    ],
  },
  {
    id: 'privacy', module: 'Setup', title: 'Data privacy requests and retention', routes: ['/privacy'], permissions: ['privacy.manage'],
    steps: [
      'When a resident asks for their data, a correction, erasure or an objection, go to Setup → Data Privacy → Record request and link their record. The target is 15 working days.',
      'For access or a copy, tap PDF (readable) or JSON (portable) on the request. Each export is logged. VAW Desk and BADAC records are disclosed only through their desks.',
      'Tap Update to complete the request, or deny it with the legal ground.',
      'Retention: set how long each kind of record is kept. Records past their period are counted under "Due now"; tap Dispose to anonymize or delete them. Each disposal is logged.',
    ],
  },
  {
    id: 'reports', module: 'Getting started', title: 'Report library', routes: ['/reports'],
    steps: [
      'Report Library (in the menu) lists every standard report you can open, grouped by module.',
      'Each PDF and Excel file states who generated it and when; the figures are as of that moment.',
      'To share figures with the city or municipality, turn on "Share figures" under Setup → Barangay Profile. Only totals are shared.',
    ],
  },
  {
    id: 'audit', module: 'Setup', title: 'Audit log', routes: ['/setup/audit'], permissions: ['audit.view'],
    steps: ['Setup → Audit Log shows who did what and when. Filter at the top by record type, action and date.', 'The log is chained and cannot be changed or deleted, even by administrators.'],
  },
];

/** The guide for the screen at `path`: the longest matching route prefix. */
export function guideFor(path: string): string | undefined {
  let best: { id: string; len: number } | undefined;
  for (const g of GUIDES) for (const r of g.routes) {
    const hit = r === '/' ? path === '/' : path === r || path.startsWith(r.endsWith('/') ? r : `${r}/`) || path.startsWith(r);
    if (hit && (!best || r.length > best.len)) best = { id: g.id, len: r.length };
  }
  return best?.id;
}
